import React, { useCallback, useEffect, useState } from 'react';

import edit from '@amzn/meridian-tokens/base/icon/edit';
import trash from '@amzn/meridian-tokens/base/icon/trash';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Icon from '@amzn/meridian/icon';
import { TableCell, TableRow } from '@amzn/meridian/table';
import Table, { type TableSortDirection } from '@amzn/meridian/table/table';
import PaginationWithDropdown from 'src/components/shared/paginationWithDropdown/paginationWithDropdown';
import UncontrolledExpandableRow from 'src/components/shared/uncontrolledExpandableRow/uncontrolledExpandableRow';
import { DEFAULT_TABLE_PAGE_SIZE } from 'src/constants/Pagination';
import { useAppSelector } from 'src/store/store';
import { type Modification } from 'src/types/modification';
import { filterSortAndPaginateTableData } from 'src/utils/tableUtils';

import { COLUMNS } from '../constants';

interface JobModificationTableProps {
  searchField: string;
  onEdit: (data: Modification) => void;
  onDelete: (data: Modification) => void;
}
const JobModificationTable = ({ searchField, onEdit, onDelete }: JobModificationTableProps) => {
  const [sortColumn, setSortColumn] = useState<keyof Modification>('name');
  const [sortDirection, setSortDirection] = useState<TableSortDirection>('ascending');
  const onSort = useCallback(
    ({ sortColumn, sortDirection }: { sortColumn: keyof Modification; sortDirection: TableSortDirection }) => {
      setSortDirection(sortDirection);
      setSortColumn(sortColumn);
    },
    [],
  );
  const modifications = useAppSelector((state) => state.modifications.modifications);
  const [filteredAndSortedTableData, setFilteredAndSortedTableData] = useState(modifications);
  const [perPage, setPerPage] = useState(DEFAULT_TABLE_PAGE_SIZE);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState<number>(0);

  useEffect(() => {
    if (!modifications.length) {
      return;
    }

    // filter, sorting, and pagination
    const { paginatedData, totalPages: newTotalPages } = filterSortAndPaginateTableData(
      modifications,
      searchField,
      sortColumn,
      sortDirection,
      currentPage,
      ['id'],
      perPage,
    );
    setFilteredAndSortedTableData(paginatedData);
    setTotalPages(newTotalPages);
  }, [modifications, searchField, sortColumn, sortDirection, currentPage, perPage]);

  useEffect(() => {
    // go back to first page when filter/sort
    setCurrentPage(1);
  }, [perPage, searchField, sortColumn, sortDirection]);

  return (
    <>
      <Box type="outline">
        <Table
          headerRows={1}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          onSort={onSort}
          showDividers={true}
          fixHeaderRows={true}
          rowComponents={[UncontrolledExpandableRow, TableRow]}
        >
          {/* Header */}
          <TableRow>
            {COLUMNS.map(({ sortKey, name, width }, idx) => (
              <TableCell width={width} key={`${idx}-${sortKey}`} sortColumn={sortKey && sortKey}>
                {name}
              </TableCell>
            ))}
          </TableRow>

          {/* Body */}
          {filteredAndSortedTableData.map((row) => {
            return (
              <UncontrolledExpandableRow key={row.id}>
                <TableCell>{row.name}</TableCell>
                <TableCell>{row.description}</TableCell>
                <TableCell>
                  <Button type="icon" aria-label={`Edit ${row.name}`} onClick={() => onEdit(row)}>
                    <Icon tokens={edit} />
                  </Button>
                </TableCell>
                <TableCell>
                  <Button type="icon" aria-label={`Delete ${row.name}`} onClick={() => onDelete(row)}>
                    <Icon tokens={trash} />
                  </Button>
                </TableCell>
              </UncontrolledExpandableRow>
            );
          })}
        </Table>
      </Box>
      {!!totalPages && (
        <PaginationWithDropdown
          totalPages={totalPages}
          currentPage={currentPage}
          perPage={perPage}
          setCurrentPage={setCurrentPage}
          setPerPage={setPerPage}
        />
      )}
    </>
  );
};

export default JobModificationTable;
